import {
  Alert,
  Popover,
  PopoverButton,
  PopoverContent,
} from "@deere/fuel-react";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import { Box, Button, Divider, IconButton, Typography } from "@mui/material";
import Badge from "@mui/material/Badge";
import { useTheme } from "@mui/material/styles";
import { TFunction } from "i18next";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  AlertsAndNotificationSource,
  AppNotificationContext,
} from "../../../../../../../context/AppNotificationContext/AppNotificationContext";
import {
  filterNotificationsToShowInHeader,
  getUnreadNotificationCount,
  hasUnreadNotifications,
} from "../../../../../../../context/AppNotificationContext/reducer/selector";
import { ConfigContext } from "../../../../../../../context/ConfigContext";
import { DealerAccountContext } from "../../../../../../../context/DealerAccountContext";
import { UserContext } from "../../../../../../../context/UserContext/UserContext";
import {
  ALL_ACCOUNTS,
  ALL_ORDERS_DOWNLOAD,
  INVOICE_BY_ACCOUNT_FILTER_CONSOLIDATION,
  INVOICE_BY_ACCOUNT_NUMBERS_CONSOLIDATION,
  INVOICE_CONSOLIDATION,
  INVOICE_FILTER,
  ORDERS_BY_LINE_ITEM_DOWNLOAD,
  RETURN_SUMMARY_EXCEL,
  RETURNS_EXCEL,
} from "../../../../../../../utils/constants";
import { downloadFileWithUrl } from "../../../../../../../utils/downloadFileWithUrl/downloadFileWithUrl";
import { INV_DOWNLOAD_ID } from "../../../../../../../utils/eventConstants";
import AllInvoicesNotification from "../../Notifications/components/FileDownloadedNotification/AllInvoicesNotification";
import ConsolidatedInvoicesNotification from "../../Notifications/components/FileDownloadedNotification/ConsolidatedInvoicesNotification";
import OrdersDownloadNotification from "../../Notifications/components/FileDownloadedNotification/OrdersDownloadNotification";
import PromotionNotification from "../../Notifications/components/FileDownloadedNotification/PromotionsNotification";
import ReturnsExcelNotification from "../../Notifications/components/FileDownloadedNotification/ReturnsExcelNotification";
import {
  DownloadNotification,
  Notification,
} from "../../Notifications/hooks/useNotifications/useNotifications";
import { mapAppNotificationListItem } from "../utils";
import NotificationItem from "./NotificationItem/NotificationItem";
import { RBACAccessContext } from "../../../../../../../context/RBACAccessContext/RBACAccessContext";
import { getOrderNotificationNames } from "../../../../../../../pages/orders/AllOrders/apiUtils";
import { isAuthorizedForMultipleAccounts } from "../../../../../../../pages/preferences/utils";

const Notifications = () => {
  const { t, i18n } = useTranslation(["common", "alertsAndNotifications"]);
  const theme = useTheme();
  const user = useContext(UserContext);
  const { dealerAccount, selectedAccounts } = useContext(DealerAccountContext);
  const { isCorporateViewEnabled } = useContext(RBACAccessContext);
  const {
    notifications,
    isRealTimeUpdatesDown,
    markNotificationsAsRead,
    markDownloadNotificationAsExpired,
  } = useContext(AppNotificationContext);
  const {
    NUMBER_OF_NOTIFICATIONS_TO_SHOW = 30,
    ENABLE_MULTIPLE_ACCOUNTS,
    ENABLE_CORPORATE_VIEW_DPA_ROLE,
    ENABLE_DELEGATE_LOGIN,
  } = useContext(ConfigContext);
  const navigate = useNavigate();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const hasUnreadNotification = hasUnreadNotifications(notifications);
  const unreadNotificationCount = getUnreadNotificationCount(notifications);
  const notificationsToShowInHeader = filterNotificationsToShowInHeader(
    notifications
  ).slice(0, NUMBER_OF_NOTIFICATIONS_TO_SHOW);

  const isMultipleAccountViewActive =
    isCorporateViewEnabled &&
    isAuthorizedForMultipleAccounts(
      user,
      ENABLE_MULTIPLE_ACCOUNTS,
      ENABLE_CORPORATE_VIEW_DPA_ROLE
    ) &&
    selectedAccounts.length > 1;
  const accountIdForDownloadNotifications = isMultipleAccountViewActive
    ? ALL_ACCOUNTS
    : (dealerAccount?.id as string);

  function markVisibleDownloadNotificationsAsRead() {
    const notificationsToMarkAsRead = notificationsToShowInHeader
      .filter(
        ({ source, isRead }) =>
          source === AlertsAndNotificationSource.DOWNLOAD_NOTIFIER && !isRead
      )
      .map(({ id, source }) => ({ id, source }));
    markNotificationsAsRead(notificationsToMarkAsRead);
  }

  const getBadgeColor = () => {
    if (!hasUnreadNotification) {
      return theme.tokens.FuelColorTextIcon;
    }
    return isRealTimeUpdatesDown
      ? theme.tokens.FuelColorAlertErrorIcon
      : theme.tokens.FuelColorAlertInfoIcon;
  };

  return (
    <Popover
      showClose={false}
      isOpen={isMenuOpen}
      onClose={() => setIsMenuOpen(false)}
    >
      <PopoverButton
        sx={{
          border: "none",
          boxShadow: "none",
        }}
        onClick={() => {
          setIsMenuOpen((prevState) => !prevState);
          markVisibleDownloadNotificationsAsRead();
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: hasUnreadNotification
              ? theme.tokens.FuelSpacing4
              : theme.tokens.FuelSpacing1,
          }}
        >
          <Box
            style={{
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <Badge
              badgeContent={unreadNotificationCount}
              max={99}
              sx={{
                "& .MuiBadge-badge": {
                  backgroundColor: getBadgeColor(),
                  color: theme.tokens.FuelColorWhite,
                },
              }}
            >
              <NotificationsOutlinedIcon
                sx={{
                  color: hasUnreadNotification
                    ? theme.tokens.FuelColorAlertInfoIcon
                    : theme.tokens.FuelColorTextIcon,
                }}
              />
            </Badge>
          </Box>
          <Typography
            sx={{
              fontSize: theme.tokens.FuelFontSizeSm,
              lineHeight: 1,
              padding: 0,
              margin: 0,
              display: "flex",
              alignItems: "center",
              fontWeight: theme.tokens.FuelFontWeightSemibold,
              color: theme.tokens.FuelColorTextTertiary,
            }}
          >
            {t("header.appNotification.notifications")}
          </Typography>
        </Box>
      </PopoverButton>
      <PopoverContent>
        <Box
          sx={{
            backgroundColor: theme.tokens.FuelColorWhite,
            borderRadius: 2,
            boxShadow: 1,
            width: "463px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: "63px",
              padding: `1.25rem ${theme.tokens.FuelSpacing4}`,
            }}
          >
            <Typography
              sx={{
                fontWeight: theme.tokens.FuelFontWeightSemibold,
                fontSize: theme.tokens.FuelFontSizeLg,
                lineHeight: "28px",
                letterSpacing: "0%",
                position: "relative",
              }}
            >
              {t("header.appNotification.notifications")}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                navigate(`/preferences`);
                setIsMenuOpen(false);
              }}
              aria-label="view preferences"
            >
              <TuneIcon />
            </IconButton>
          </Box>

          <Divider
            sx={{
              mx: "auto",
              borderBottomWidth: "1px",
              opacity: 1,
              position: "relative",
            }}
          />

          {isRealTimeUpdatesDown && (
            <Box
              sx={{
                paddingX: theme.tokens.FuelSpacing4,
                paddingTop: theme.tokens.FuelSpacing4,
              }}
            >
              <Alert variant="error">
                {t("header.notifications.realTimeUpdatesInactiveMessage")}
              </Alert>
            </Box>
          )}

          {notificationsToShowInHeader.length === 0 ? (
            <Box marginY={theme.tokens.FuelSpacing12}>
              <Typography
                variant="h3"
                sx={{
                  textAlign: "center",
                  color: theme.tokens.FuelColorTextDisabled,
                }}
              >
                {t("header.notifications.noNotifications")}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                position: "relative",
                overflowY: "auto",
                maxHeight: 392,
                padding: theme.tokens.FuelSpacing4,
              }}
            >
              {notificationsToShowInHeader.map((notificationItem) => {
                if (
                  notificationItem.source ===
                  AlertsAndNotificationSource.DOWNLOAD_NOTIFIER
                ) {
                  return (
                    <React.Fragment key={notificationItem.id}>
                      {getDownloadNotificationItem(
                        notificationItem.payload as Notification,
                        markDownloadNotificationAsExpired,
                        accountIdForDownloadNotifications,
                        t
                      )}
                    </React.Fragment>
                  );
                }
                const appNotificationListItem = mapAppNotificationListItem(
                  notificationItem,
                  i18n.language,
                  t,
                  isMultipleAccountViewActive,
                  navigate,
                  markNotificationsAsRead,
                  ENABLE_DELEGATE_LOGIN
                );
                return (
                  <NotificationItem
                    key={appNotificationListItem.id}
                    {...appNotificationListItem}
                    onButtonClick={() => {
                      setIsMenuOpen(false);
                      appNotificationListItem.onButtonClick();
                    }}
                  />
                );
              })}
            </Box>
          )}

          <Divider />

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              px: "119px",
              py: "16px",
            }}
          >
            <Button
              onClick={() => {
                navigate(`/alerts-notifications-center`);
                setIsMenuOpen(false);
              }}
              sx={{
                border: "none",
                boxShadow: "none",
              }}
            >
              <Typography
                sx={{
                  fontWeight: theme.tokens.FuelFontWeightSemibold,
                  fontSize: theme.tokens.FuelFontSizeSm,
                  lineHeight: "1.25rem",
                  letterSpacing: "0%",
                  color: theme.tokens.FuelColorJdGreen,
                  alignItems: "center",
                }}
              >
                {t("header.appNotification.viewAllAlertsAndNotifications")}
              </Typography>
            </Button>
          </Box>
        </Box>
      </PopoverContent>
    </Popover>
  );
};

export default Notifications;

const getDownloadNotificationItem = (
  downloadNotification: Notification<DownloadNotification>,
  expireFunction: (type: string, createdTimeInMs: number) => void,
  accountId: string,
  t: TFunction
) => {
  const { filename, type } = downloadNotification.payload;
  const expireNotificationFunction = (createdTimeInMs: number) => {
    expireFunction(type ?? "", createdTimeInMs);
  };

  if (type === ORDERS_BY_LINE_ITEM_DOWNLOAD) {
    return (
      <OrdersDownloadNotification
        notification={downloadNotification}
        accountId={accountId}
        downloadFile={downloadFileWithUrl}
        expireNotification={expireNotificationFunction}
        successMessage={t(
          "notifications.orderByLineItem.download.success.notification"
        )}
        failureMessage={t(
          "notifications.orderByLineItem.download.failure.message"
        )}
      />
    );
  }
  if (
    type === INVOICE_CONSOLIDATION ||
    type === INVOICE_FILTER ||
    type === INVOICE_BY_ACCOUNT_NUMBERS_CONSOLIDATION ||
    type === INVOICE_BY_ACCOUNT_FILTER_CONSOLIDATION ||
    (filename?.startsWith(INV_DOWNLOAD_ID) &&
      type !== ORDERS_BY_LINE_ITEM_DOWNLOAD)
  ) {
    return (
      <ConsolidatedInvoicesNotification
        notification={downloadNotification}
        accountId={accountId}
        expireNotification={expireNotificationFunction}
        downloadFile={downloadFileWithUrl}
      />
    );
  }

  if (type === "Invoice") {
    return (
      <AllInvoicesNotification
        notification={downloadNotification}
        accountId={accountId}
        expireNotification={expireNotificationFunction}
        downloadFile={downloadFileWithUrl}
      />
    );
  }

  if (type === ALL_ORDERS_DOWNLOAD) {
    const { displayName, fileName } = getOrderNotificationNames(
      true,
      downloadNotification.payload.filename,
      downloadNotification.payload.displayName,
      accountId,
      downloadNotification.createdAt
    );

    const modifiedNotification = {
      ...downloadNotification,
      payload: {
        ...downloadNotification.payload,
        filename: fileName,
        displayName,
      },
    };

    return (
      <OrdersDownloadNotification
        notification={modifiedNotification}
        accountId={accountId}
        expireNotification={expireNotificationFunction}
        downloadFile={downloadFileWithUrl}
        successMessage={t(
          "notifications.allOrders.download.success.notification",
          { fileName }
        )}
        failureMessage={t("notifications.allOrders.download.failure.message", {
          fileName,
        })}
      />
    );
  }

  if (type === RETURNS_EXCEL || type === RETURN_SUMMARY_EXCEL) {
    return (
      <ReturnsExcelNotification
        notification={downloadNotification}
        accountId={accountId}
        expireNotification={expireNotificationFunction}
        downloadFile={downloadFileWithUrl}
      />
    );
  }

  return (
    <PromotionNotification
      notification={downloadNotification}
      downloadFile={downloadFileWithUrl}
      expireNotification={expireNotificationFunction}
    />
  );
};
